import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

interface OnboardingReadinessScreenProps {
  isError: boolean;
  isStuck?: boolean;
  onContinue?: () => void;
  /**
   * Re-polls provisioning status. Recovers a UI stuck on stale state.
   * Does NOT re-drive a failed provisioning saga — button copy must reflect this.
   */
  onCheckAgain?: () => void;
}

export function OnboardingReadinessScreen({
  isError,
  isStuck,
  onContinue,
  onCheckAgain,
}: OnboardingReadinessScreenProps) {
  if (isError) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center max-w-md px-6 flex flex-col gap-4">
          <Text variant="headline-sm" as="h1">
            Setup check failed
          </Text>
          <Text variant="body-md" color="muted">
            We could not check if your workspace is ready. Please refresh the page or try again in a
            few minutes.
          </Text>
          <Button
            type="button"
            variant="outline"
            onClick={() => window.location.reload()}
            className="mx-auto"
          >
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  if (isStuck) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-center max-w-md px-6 flex flex-col gap-4">
          <Text variant="headline-sm" as="h1">
            This is taking longer than expected
          </Text>
          <Text variant="body-md" color="muted">
            Workspace setup is still in progress. You can wait and refresh, or continue to the app
            now — some features may be unavailable until setup completes.
          </Text>
          <div className="flex flex-col items-center gap-2">
            {onContinue && (
              <Button
                type="button"
                onClick={onContinue}
                className="w-full max-w-xs"
              >
                Continue to app
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => window.location.reload()}
              className="w-full max-w-xs"
            >
              Refresh
            </Button>
          </div>
          <Text variant="body-sm" color="subtle">
            Still waiting?{' '}
            <a
              href="mailto:support@entite.io"
              className="text-primary underline-offset-2 hover:underline"
            >
              Contact support
            </a>
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="text-center max-w-md px-6 flex flex-col items-center gap-6">
        <Spinner size="lg" className="text-primary" />
        <div className="flex flex-col gap-2">
          <Text variant="headline-sm" as="h1">
            Setting up your workspace
          </Text>
          <Text variant="body-md" color="muted">
            This usually takes less than a minute. You'll receive an email when your account is
            ready to use.
          </Text>
        </div>
        <Text variant="body-sm" color="subtle">
          You can safely close this tab and return later — we'll send you an email when everything
          is ready.
        </Text>
        {onCheckAgain && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCheckAgain}
            data-testid="check-again-btn"
          >
            Check again
          </Button>
        )}
      </div>
    </div>
  );
}
